import type { DraftDateRange, DraftPassengers, FlightDraft } from '@massilia/contracts'

import { cn } from '@massilia/ui'
import { useState } from 'react'

import { isChatDraftDebugEnabled } from '@/lib/feature-flags'
import { useChatWidgetStore } from '@/stores/chat-widget.store'

/** Une ligne du tableau : la case, son libellé, sa valeur lisible. */
interface DraftRow {
  label: string
  slot: string
  /** Absente = case vide. Affichée « — », en gris. */
  value?: string
}

const TRIP_TYPE_LABELS = {
  multi_city: 'Multi-destinations',
  one_way: 'Aller simple',
  round_trip: 'Aller-retour',
} satisfies Record<NonNullable<FlightDraft['tripType']>, string>

/** Singuliers seulement : les cinq catégories font toutes leur pluriel en « s ». */
const PASSENGER_LABELS = {
  adults: 'adulte',
  babies: 'bébé',
  infants: 'enfant',
  seniors: 'senior',
  youngsters: 'jeune',
} satisfies Record<keyof DraftPassengers, string>

/**
 * La fiche de recherche vol pendant qu'elle se remplit — outil de dev.
 *
 * Deux vues : le tableau des cases DANS L'ORDRE OÙ `nextQuestion` les demande,
 * qui montre d'un coup d'œil où la collecte en est, et le JSON brut pour
 * inspecter ce que le serveur a réellement renvoyé.
 *
 * Ne coûte aucune requête : `flightModeState` est déjà dans le store, le
 * serveur le renvoie à chaque tour dans `nextClientState`.
 */
export function FlightDraftDebugPanel() {
  const flightModeState = useChatWidgetStore((state) => state.flightModeState)
  const [showJson, setShowJson] = useState(false)

  if (!isChatDraftDebugEnabled) return null

  const draft = flightModeState?.draft
  const pendingSlot = flightModeState?.pendingSlot
  const rows = draft ? buildRows(draft) : []
  const filled = rows.filter((row) => row.value !== undefined).length

  return (
    <details className="border-t border-amber-200/70 bg-amber-50/50 text-[11px] text-amber-950">
      <summary className="flex cursor-pointer items-center gap-1.5 px-3 py-1.5 font-semibold select-none">
        <span>🛠 Fiche vol</span>
        <span className="font-normal text-amber-700">
          {filled}/{rows.length} cases
        </span>
        {/* La case en attente est répétée ici pour être lisible panneau FERMÉ :
            c'est l'information qu'on regarde le plus souvent, et l'ouvrir à
            chaque tour pour la lire n'a pas de sens. */}
        {pendingSlot ? (
          <span className="font-mono text-[10px] font-normal text-amber-600">◀ {pendingSlot}</span>
        ) : null}
        {flightModeState?.step ? (
          <span className="ml-auto rounded-full bg-amber-200/60 px-2 py-0.5 font-mono text-[10px] font-normal">
            {flightModeState.step}
          </span>
        ) : null}
      </summary>

      <div className="px-3 pb-2">
        <button
          className="mb-1.5 rounded-md border border-amber-300/60 bg-white/60 px-2 py-0.5 text-[10px] font-medium transition hover:bg-white"
          onClick={() => setShowJson((current) => !current)}
          type="button"
        >
          {showJson ? '← Fiche' : 'JSON →'}
        </button>

        {showJson ? (
          <pre className="max-h-56 overflow-auto rounded-md bg-white/70 p-2 font-mono text-[10px] leading-tight whitespace-pre-wrap">
            {draft ? JSON.stringify(draft, null, 2) : 'aucune fiche'}
          </pre>
        ) : (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 rounded-md bg-white/70 p-2">
            {rows.map((row) => {
              const isPending = row.slot === pendingSlot
              return (
                <div className="contents" key={row.slot}>
                  <dt
                    className={cn(
                      'text-right text-amber-700',
                      isPending && 'font-semibold text-amber-900'
                    )}
                  >
                    {row.label}
                  </dt>
                  <dd
                    className={cn(
                      // `whitespace-pre-line` : les étapes sont une ligne par trajet.
                      'font-mono whitespace-pre-line',
                      row.value === undefined && 'text-amber-400',
                      isPending && 'font-semibold'
                    )}
                  >
                    {row.value ?? '—'}
                    {/* La case en attente est la SEULE information que le
                        tableau ne porte pas déjà : sans elle, une fiche à
                        moitié vide ne dit pas laquelle des cases vides est en
                        train d'être demandée. */}
                    {isPending ? <span className="ml-1.5 text-amber-600">◀ demandé</span> : null}
                  </dd>
                </div>
              )
            })}
          </dl>
        )}
      </div>
    </details>
  )
}

/**
 * Les cases DANS L'ORDRE de `nextQuestion` (serveur) — c'est ce qui rend le
 * tableau lisible : la première ligne vide est celle qui va être demandée.
 * Retour et étapes n'apparaissent que sur le type de trajet qui les concerne,
 * comme côté serveur.
 */
function buildRows(draft: FlightDraft): DraftRow[] {
  const rows: DraftRow[] = [
    { label: 'Destination', slot: 'destination', value: draft.destination?.cityName },
    { label: 'Départ', slot: 'departure', value: draft.departure?.cityName },
    {
      label: 'Trajet',
      slot: 'tripType',
      value: draft.tripType ? TRIP_TYPE_LABELS[draft.tripType] : undefined,
    },
    { label: 'Aller', slot: 'outbound', value: formatRange(draft.outbound) },
  ]

  if (draft.tripType === 'round_trip') {
    rows.push({ label: 'Retour', slot: 'return', value: formatRange(draft.return) })
  }

  if (draft.tripType === 'multi_city') {
    rows.push({ label: 'Étapes', slot: 'stops', value: formatStops(draft) })
  }

  rows.push(
    { label: 'Passagers', slot: 'passengers', value: formatPassengers(draft.passengers) },
    { label: 'Bagage', slot: 'luggage', value: formatBoolean(draft.luggage) },
    {
      label: 'Flexibilité',
      slot: 'flexibility',
      value: draft.flexibilityAsked ? 'demandée' : undefined,
    }
  )

  if (draft.filters && Object.keys(draft.filters).length > 0) {
    rows.push({
      label: 'Filtres',
      slot: 'filters',
      value: Object.entries(draft.filters)
        .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join(',') : String(value)}`)
        .join(' · '),
    })
  }

  return rows
}

function formatBoolean(value?: boolean): string | undefined {
  if (value === undefined) return undefined
  return value ? 'oui' : 'non'
}

/** `2026-09-30` → `30/09`. L'année n'est répétée qu'une fois, en fin de plage. */
const formatDay = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`

function formatPassengers(passengers?: DraftPassengers): string | undefined {
  if (!passengers) return undefined
  const parts = Object.entries(PASSENGER_LABELS)
    .map(([key, label]) => ({ count: passengers[key as keyof DraftPassengers], label }))
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => `${count} ${label}${count > 1 ? 's' : ''}`)
  return parts.length > 0 ? parts.join(', ') : 'aucun'
}

/** Une date exacte, ou la plage. Le format ISO brut reste lisible dans l'onglet JSON. */
function formatRange(range?: DraftDateRange): string | undefined {
  if (!range) return undefined
  const year = range.to.slice(0, 4)
  return range.from === range.to
    ? `${formatDay(range.from)}/${year}`
    : `${formatDay(range.from)} → ${formatDay(range.to)}/${year}`
}

/** Une étape par ligne, dans l'ordre du voyage, avec l'open-jaw quand il y en a un. */
function formatStops(draft: FlightDraft): string | undefined {
  const stops = draft.stops ?? []
  if (stops.length === 0) return undefined
  const lines = stops.map((stop) => {
    const from = stop.departure ? `${stop.departure.cityName} → ` : ''
    return `${from}${stop.destination.cityName} · ${formatRange(stop.dates) ?? 'date ?'}`
  })
  return `${lines.join('\n')}${draft.stopsDone ? '' : '\n(itinéraire en cours)'}`
}
